import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TbLink, TbPhoto, TbTrash, TbX } from 'react-icons/tb';
import ModalLayout from '../layouts/ModalLayout';
import CancelSaveButton from '../misc/CancelSaveButton';
import Input from '../misc/Input';
import Button from '../buttons/Button';
import { Post, PostLinkedFeatureType } from '../../../types/post';
import postService from '../../../services/community/postService';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { CommunityFeature } from '../../../types/community';
import { taskService } from '../../../services/community/taskService';
import { getAllScoreCards } from '../../../services/community/listService';
import photoAlbumService from '../../../services/community/photoAlbumService';
import eventService from '../../../services/community/eventService';
import formsService from '../../../services/community/formsService';
import { Task } from '../../../models/taskService.model';
import { ScoreCard } from '../../../models/scoreCard.model';
import { Event } from '../../../types/event';
import { Form } from '../../../models/forms.model';

type ImageEntry = { type: 'url'; url: string } | { type: 'file'; file: File; preview: string };

const revokeFilePreviews = (entries: ImageEntry[]) => {
  entries.forEach((entry) => {
    if (entry.type === 'file') URL.revokeObjectURL(entry.preview);
  });
};

type LinkedItem = {
  id: string;
  label: string;
  subtitle?: string;
};

const LINKED_OPTIONS: Array<{
  type: PostLinkedFeatureType;
  feature: CommunityFeature;
}> = [
  { type: PostLinkedFeatureType.EVENT, feature: CommunityFeature.EVENTS },
  { type: PostLinkedFeatureType.TASK, feature: CommunityFeature.TASKS },
  { type: PostLinkedFeatureType.SCORECARD, feature: CommunityFeature.STATS_AND_SCORES },
  { type: PostLinkedFeatureType.PHOTO_ALBUM, feature: CommunityFeature.PHOTO_ALBUMS },
  { type: PostLinkedFeatureType.FORM, feature: CommunityFeature.FORMS },
];

interface CreateEditPostModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  post?: Post;
  onSuccess: () => void;
}

export default function CreateEditPostModal({
  open,
  setOpen,
  post,
  onSuccess,
}: CreateEditPostModalProps): JSX.Element {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const isEditing = !!post;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imagesRef = useRef<ImageEntry[]>([]);

  const [content, setContent] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [images, setImages] = useState<ImageEntry[]>([]);
  const [selectedLinkedType, setSelectedLinkedType] = useState<PostLinkedFeatureType | null>(null);
  const [selectedLinkedItemId, setSelectedLinkedItemId] = useState<string | null>(null);
  const [linkedPickerType, setLinkedPickerType] = useState<PostLinkedFeatureType | null>(null);
  const [linkedSearch, setLinkedSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [scorecards, setScorecards] = useState<ScoreCard[]>([]);
  const [albums, setAlbums] = useState<{ id: string; title: string; photoCount: number }[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [forms, setForms] = useState<Form[]>([]);

  imagesRef.current = images;

  useEffect(() => {
    if (!open) {
      revokeFilePreviews(imagesRef.current);
      return;
    }

    revokeFilePreviews(imagesRef.current);

    if (post) {
      setContent(post.content ?? '');
      setExternalUrl(post.externalUrl ?? '');
      setShowUrlInput(!!post.externalUrl);
      setImages(post.images.map((img) => ({ type: 'url' as const, url: img.url })));
      setSelectedLinkedType(post.linkedFeatureType);
      setSelectedLinkedItemId(
        post.linkedFormId ??
          post.linkedPhotoAlbumId ??
          (post.linkedFeatureId != null ? String(post.linkedFeatureId) : null),
      );
    } else {
      setContent('');
      setExternalUrl('');
      setShowUrlInput(false);
      setImages([]);
      setSelectedLinkedType(null);
      setSelectedLinkedItemId(null);
    }
    setLinkedPickerType(null);
    setLinkedSearch('');
  }, [open, post]);

  useEffect(() => {
    if (!open || !community?.id) return;

    const load = async () => {
      try {
        const [taskList, scoreList, albumList, eventList, formList] = await Promise.all([
          community.features?.[CommunityFeature.TASKS]
            ? taskService.getAllTasksForCommunity(community.id)
            : Promise.resolve([]),
          community.features?.[CommunityFeature.STATS_AND_SCORES]
            ? getAllScoreCards()
            : Promise.resolve([]),
          community.features?.[CommunityFeature.PHOTO_ALBUMS]
            ? photoAlbumService.getPhotoAlbums(community.id)
            : Promise.resolve([]),
          community.features?.[CommunityFeature.EVENTS]
            ? eventService.getUpcomingEvents()
            : Promise.resolve([]),
          community.features?.[CommunityFeature.FORMS]
            ? formsService.list()
            : Promise.resolve([]),
        ]);
        setTasks(taskList);
        setScorecards(scoreList);
        setAlbums(albumList.map((a) => ({ id: a.id, title: a.title, photoCount: a.photoCount })));
        setEvents(eventList);
        setForms(formList);
      } catch {
        toast.error(t('page.communicationOverview.posts.modal.error'));
      }
    };

    load();
  }, [open, community?.id, community?.features, t]);

  const enabledLinkedOptions = LINKED_OPTIONS.filter(
    (opt) => community?.features?.[opt.feature],
  );

  const getItemsForType = (type: PostLinkedFeatureType | null): LinkedItem[] => {
    if (!type) return [];
    switch (type) {
      case PostLinkedFeatureType.TASK:
        return tasks.map((task) => ({
          id: String(task.taskId),
          label: task.name || task.description || '',
          subtitle: task.dueDate ? new Date(task.dueDate).toLocaleDateString() : undefined,
        }));
      case PostLinkedFeatureType.SCORECARD:
        return scorecards.map((s) => ({
          id: String(s.scoreCardId),
          label: s.name,
          subtitle: t('page.communicationOverview.posts.modal.linkedScorecardMembers', {
            count: s.scores.length,
          }),
        }));
      case PostLinkedFeatureType.PHOTO_ALBUM:
        return albums.map((a) => ({
          id: a.id,
          label: a.title,
          subtitle: t('page.communicationOverview.posts.modal.linkedAlbumPhotos', {
            count: a.photoCount,
          }),
        }));
      case PostLinkedFeatureType.EVENT:
        return events.map((e) => ({
          id: String(e.id),
          label: e.title,
          subtitle:
            [e.startTime ? new Date(e.startTime).toLocaleDateString() : null, e.location]
              .filter(Boolean)
              .join(' · ') || undefined,
        }));
      case PostLinkedFeatureType.FORM:
        return forms.map((f) => ({
          id: f.id,
          label: f.title,
          subtitle: f.deadline ? new Date(f.deadline).toLocaleDateString() : undefined,
        }));
      default:
        return [];
    }
  };

  const pickerItems = getItemsForType(linkedPickerType).filter((item) =>
    linkedSearch.trim()
      ? item.label.toLowerCase().includes(linkedSearch.toLowerCase())
      : true,
  );

  const selectedLinkedLabel =
    selectedLinkedType && selectedLinkedItemId
      ? getItemsForType(selectedLinkedType).find((i) => i.id === selectedLinkedItemId)?.label
      : null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const remaining = 10 - images.length;
    const toAdd = files.slice(0, remaining).map((file) => ({
      type: 'file' as const,
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...toAdd]);
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setImages((prev) => {
      const entry = prev[index];
      if (entry?.type === 'file') URL.revokeObjectURL(entry.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async () => {
    if (
      !content.trim() &&
      !selectedLinkedItemId &&
      !externalUrl.trim() &&
      images.length === 0
    ) {
      toast.error(t('page.communicationOverview.posts.modal.emptyError'));
      return;
    }

    const trimmedExternalUrl = externalUrl.trim();
    const fullUrl =
      trimmedExternalUrl && !trimmedExternalUrl.startsWith('http')
        ? `https://${trimmedExternalUrl}`
        : trimmedExternalUrl || undefined;

    setLoading(true);
    try {
      const existingUrls = images
        .filter((img): img is { type: 'url'; url: string } => img.type === 'url')
        .map((img) => img.url);
      const newFiles = images
        .filter((img): img is { type: 'file'; file: File; preview: string } => img.type === 'file')
        .map((img) => img.file);

      let imageUrls = existingUrls;
      if (newFiles.length > 0) {
        const uploaded = await postService.uploadPostImages(newFiles);
        imageUrls = [...imageUrls, ...uploaded];
        revokeFilePreviews(images);
      }

      if (isEditing && post) {
        await postService.updatePost(post.postId, {
          content: content.trim(),
          externalUrl: fullUrl ?? undefined,
          imageUrls,
        });
      } else {
        await postService.createPost({
          content: content.trim() || undefined,
          externalUrl: fullUrl,
          linkedFeatureType: selectedLinkedType ?? undefined,
          linkedFeatureId:
            selectedLinkedType &&
            selectedLinkedType !== PostLinkedFeatureType.PHOTO_ALBUM &&
            selectedLinkedType !== PostLinkedFeatureType.FORM &&
            selectedLinkedItemId != null
              ? Number(selectedLinkedItemId)
              : undefined,
          linkedPhotoAlbumId:
            selectedLinkedType === PostLinkedFeatureType.PHOTO_ALBUM
              ? selectedLinkedItemId ?? undefined
              : undefined,
          linkedFormId:
            selectedLinkedType === PostLinkedFeatureType.FORM
              ? selectedLinkedItemId ?? undefined
              : undefined,
          imageUrls,
        });
      }
      onSuccess();
    } catch {
      toast.error(t('page.communicationOverview.posts.modal.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-2xl bg-secondary-50 sm:max-w-[640px]">
      <div className="border-b border-secondary-200 px-8 py-5">
        <h2 className="text-lg font-semibold text-primary-900">
          {isEditing
            ? t('page.communicationOverview.posts.modal.editTitle')
            : t('page.communicationOverview.posts.modal.createTitle')}
        </h2>
      </div>

      <div className="flex flex-col gap-5 p-8">
        <Input
          id="post-content"
          type="textarea"
          label={t('page.communicationOverview.posts.modal.content')}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t('page.communicationOverview.posts.modal.contentPlaceholder')}
          className="min-h-32"
        />

        <div className="flex flex-col gap-3">
          <p className="font-medium">{t('page.communicationOverview.posts.modal.images')}</p>
          {images.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {images.map((img, index) => (
                <div key={index} className="relative">
                  <img
                    src={img.type === 'url' ? img.url : img.preview}
                    alt=""
                    className="h-24 w-24 rounded-xl object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -right-1 -top-1 rounded-full bg-danger p-1 text-secondary-50"
                  >
                    <TbX className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {images.length < 10 && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                  <TbPhoto className="h-5 w-5" />
                  {t('page.communicationOverview.posts.modal.addImages')}
                </Button>
              </>
            )}
            {!showUrlInput && (
              <Button variant="secondary" onClick={() => setShowUrlInput(true)}>
                <TbLink className="h-5 w-5" />
                {t('page.communicationOverview.posts.modal.addLink')}
              </Button>
            )}
          </div>
          {images.length > 0 && (
            <p className="text-sm text-secondary-600">
              {t('page.communicationOverview.posts.modal.imageCount', {
                count: images.length,
                max: 10,
              })}
            </p>
          )}
        </div>

        {showUrlInput && (
          <div className="flex items-end gap-2">
            <Input
              id="post-external-url"
              type="text"
              label={t('page.communicationOverview.posts.modal.externalUrl')}
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://"
              className="flex-1"
            />
            <Button
              variant="secondary"
              onClick={() => {
                setShowUrlInput(false);
                setExternalUrl('');
              }}
            >
              <TbTrash className="h-5 w-5" />
            </Button>
          </div>
        )}

        {!isEditing && enabledLinkedOptions.length > 0 && (
          <div className="flex flex-col gap-3">
            <p className="font-medium">{t('page.communicationOverview.posts.modal.linkedFeature')}</p>
            {selectedLinkedType && selectedLinkedItemId ? (
              <div className="flex items-center justify-between rounded-lg border border-primary-200 px-3 py-2">
                <span className="text-sm font-medium">
                  {t(`page.communicationOverview.posts.linkedTypes.${selectedLinkedType}`)}
                  {selectedLinkedLabel ? `: ${selectedLinkedLabel}` : ''}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSelectedLinkedType(null);
                    setSelectedLinkedItemId(null);
                  }}
                >
                  <TbX className="h-4 w-4" />
                </Button>
              </div>
            ) : linkedPickerType ? (
              <div className="rounded-lg border border-primary-200 p-3">
                <p className="mb-2 text-sm font-medium">
                  {t(`page.communicationOverview.posts.linkedTypes.${linkedPickerType}`)}
                </p>
                <Input
                  id="post-linked-search"
                  type="text"
                  value={linkedSearch}
                  onChange={(e) => setLinkedSearch(e.target.value)}
                  placeholder={t('page.communicationOverview.posts.modal.searchLinked')}
                />
                <div className="mt-2 max-h-48 overflow-y-auto">
                  {pickerItems.length === 0 ? (
                    <p className="py-2 text-sm text-secondary-600">
                      {t('page.communicationOverview.posts.modal.noItems')}
                    </p>
                  ) : (
                    pickerItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="flex w-full flex-col rounded-md px-2 py-2 text-left hover:bg-primary-100"
                        onClick={() => {
                          setSelectedLinkedType(linkedPickerType);
                          setSelectedLinkedItemId(item.id);
                          setLinkedPickerType(null);
                          setLinkedSearch('');
                        }}
                      >
                        <span className="text-sm font-medium">{item.label}</span>
                        {item.subtitle && (
                          <span className="text-xs text-secondary-600">{item.subtitle}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
                <Button variant="tertiary" className="mt-2" onClick={() => setLinkedPickerType(null)}>
                  {t('general.cancel')}
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {enabledLinkedOptions.map((opt) => (
                  <Button
                    key={opt.type}
                    variant="secondary"
                    onClick={() => {
                      setLinkedPickerType(opt.type);
                      setLinkedSearch('');
                    }}
                  >
                    {t(`page.communicationOverview.posts.linkedTypes.${opt.type}`)}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {isEditing && post?.linkedFeatureType && (
          <p className="text-sm italic text-secondary-600">
            {t('page.communicationOverview.posts.modal.linkedFeatureEditNote')}
          </p>
        )}

        <CancelSaveButton
          onCancel={() => setOpen(false)}
          onSave={handleSubmit}
          loading={loading}
          className="self-end"
          buttonText={
            isEditing
              ? t('page.communicationOverview.posts.modal.save')
              : t('page.communicationOverview.posts.modal.publish')
          }
        />
      </div>
    </ModalLayout>
  );
}
